import styled from '@emotion/styled';
import { Button, PhoneIcon24 } from '@landsbankinn/ui-kit';
import {
  type PointerEvent,
  type RefObject,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useDebugUrl } from '../hooks/useSetDebugUrl';

// Pointer travel below this is treated as a click rather than a drag.
const DRAG_THRESHOLD_PX = 4;
const VIEWPORT_INSET_PX = 15;

type Edge = 'top' | 'right' | 'bottom' | 'left';
type Alignment = Edge | 'center';

/**
 * `<outside|inside>+<side>+<alignment>`, e.g. `outside+top+left` sits above the
 * anchor with their left edges aligned. The side and alignment are always on
 * opposite axes. Without an anchor the viewport is used, so only `inside`
 * placements are visible.
 */
export type AnchorPlacement =
  | `${'outside' | 'inside'}+${'top' | 'bottom'}+${'left' | 'center' | 'right'}`
  | `${'outside' | 'inside'}+${'left' | 'right'}+${'top' | 'center' | 'bottom'}`;

/**
 * Percentage of the button's own size to shift by, so placement needs no
 * measurement of the button itself. Flush puts its matching edge on the
 * anchor's edge; outward pushes it past that edge.
 */
function shiftPercent(edge: Edge, outward: boolean) {
  const isStartEdge = edge === 'top' || edge === 'left';
  return isStartEdge === outward ? -100 : 0;
}

function getAnchoredStart(rect: DOMRect, placement: AnchorPlacement) {
  const [mode, side, alignment] = placement.split('+') as [
    string,
    Edge,
    Alignment,
  ];
  const sideIsHorizontal = side === 'left' || side === 'right';

  const sideOffset = {
    at: rect[side],
    shift: shiftPercent(side, mode === 'outside'),
  };
  const alignOffset =
    alignment === 'center'
      ? {
          at: sideIsHorizontal
            ? rect.top + rect.height / 2
            : rect.left + rect.width / 2,
          shift: -50,
        }
      : { at: rect[alignment], shift: shiftPercent(alignment, false) };

  const [xOffset, yOffset] = sideIsHorizontal
    ? [sideOffset, alignOffset]
    : [alignOffset, sideOffset];

  return {
    position: { x: xOffset.at, y: yOffset.at },
    transform: `translate(${xOffset.shift}%, ${yOffset.shift}%)`,
  };
}

function getViewportRect() {
  return new DOMRect(
    VIEWPORT_INSET_PX,
    VIEWPORT_INSET_PX,
    window.innerWidth - 2 * VIEWPORT_INSET_PX,
    window.innerHeight - 2 * VIEWPORT_INSET_PX,
  );
}

const Floating = styled.div`
  position: fixed;
  z-index: ${({ theme }) => theme.zIndexConfig.fab};
  touch-action: none;
  cursor: grab;

  &:active {
    cursor: grabbing;
  }

  /* Button has no borderRadius prop, only the customVariant buttons do. */
  button {
    border-radius: 1rem;
    animation: flashEffect 2s ease-out 1 forwards;
  }
`;

type Point = { x: number; y: number };

export function MockAuthenticator({
  anchorRef,
  placement = 'inside+top+center',
}: {
  /** Followed until the user drags the button, after which it floats freely. */
  anchorRef?: RefObject<HTMLElement | null>;
  /** Position relative to `anchorRef`, or to the viewport without one. */
  placement?: AnchorPlacement;
}) {
  const debugUrl = useDebugUrl();

  const [position, setPosition] = useState<Point>();
  const [transform, setTransform] = useState<string>();
  const [isTethered, setIsTethered] = useState(true);
  const dragOrigin = useRef<{ pointer: Point; position: Point }>();
  const hasDragged = useRef(false);

  // Passive, not layout: the anchor is an ancestor, whose ref is only attached
  // once the whole commit is done.
  useEffect(() => {
    if (!isTethered) return;

    const update = () => {
      const rect =
        anchorRef?.current?.getBoundingClientRect() ?? getViewportRect();
      const next = getAnchoredStart(rect, placement);
      setPosition(next.position);
      setTransform(next.transform);
    };
    update();

    // Capture phase so scrolling in nested containers is picked up too.
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    const observer = new ResizeObserver(update);
    if (anchorRef?.current) observer.observe(anchorRef.current);

    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
      observer.disconnect();
    };
  }, [anchorRef, placement, isTethered]);

  if (!debugUrl || !position) return null;

  const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    dragOrigin.current = { pointer: { x: e.clientX, y: e.clientY }, position };
    hasDragged.current = false;
  };

  const onPointerMove = (e: PointerEvent<HTMLDivElement>) => {
    const origin = dragOrigin.current;
    if (!origin) return;
    const dx = e.clientX - origin.pointer.x;
    const dy = e.clientY - origin.pointer.y;
    if (Math.hypot(dx, dy) > DRAG_THRESHOLD_PX) {
      hasDragged.current = true;
      setIsTethered(false);
    }
    setPosition({ x: origin.position.x + dx, y: origin.position.y + dy });
  };

  const onPointerUp = () => {
    dragOrigin.current = undefined;
  };

  const onClick = () => {
    if (!hasDragged.current) window.open(debugUrl, '_blank');
  };

  return (
    <Floating
      style={{ left: position.x, top: position.y, transform }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      // Pointer capture retargets the click to this wrapper,
      // so the button can't handle it itself.
      onClick={onClick}
    >
      <Button
        instrumentAs="mock-authenticate"
        height="48"
        paddingHorizontal="10"
        startIcon={<PhoneIcon24 />}
        variant="primary"
      >
        Mock authenticate
      </Button>
    </Floating>
  );
}
